import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, CATEGORY_LABELS } from "@/lib/api";

export default function MenuDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api
      .get("/wines")
      .then((res) => {
        const map = {};
        res.data.forEach((w) => (map[w.category] = (map[w.category] || 0) + 1));
        setCounts(map);
      })
      .catch(() => {});
  }, []);

  const go = (path) => {
    onClose();
    navigate(path);
  };

  const links = [
    { id: "menu-home", label: "Accueil", path: "/" },
    { id: "menu-catalogue", label: "Toute la cave", path: "/vins" },
    ...Object.entries(CATEGORY_LABELS).map(([id, label]) => ({
      id: `menu-cat-${id}`,
      label,
      path: `/vins?cat=${id}`,
      indent: true,
      empty: !counts[id],
    })),
    { id: "menu-pickup", label: "Enlèvement à Belfort", path: "/#enlevement" },
    { id: "menu-reserve", label: "Réserver une table", path: "/reserver" },
    { id: "menu-profile", label: user ? "Mon profil" : "Se connecter", path: "/profil" },
  ];
  if (user?.role === "admin") {
    links.push({ id: "menu-admin", label: "Administration", path: "/admin" });
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="menu-drawer">
          <div className="absolute inset-0 bg-[#1C1917]/50 backdrop-blur-sm" onClick={onClose} />
          <motion.aside
            className="absolute left-0 top-0 h-full w-[85%] max-w-xs bg-[#FEF9F6] shadow-2xl flex flex-col"
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#6B1226]/10">
              <div className="flex flex-col items-start">
                <span className="font-logo-caps text-[0.55rem] tracking-[0.32em] text-[#6B1226]">LES VINS DU</span>
                <span className="font-logo text-xl font-bold text-[#1C1917] tracking-[0.08em]">BISTROQUET</span>
              </div>
              <button onClick={onClose} className="p-2 text-[#1C1917] hover:text-[#6B1226] transition-colors" data-testid="menu-close-button" aria-label="Fermer le menu">
                <X size={22} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-6 py-6" data-lenis-prevent>
              <ul className="space-y-1">
                {links.map((link, i) => (
                  <li key={link.id}>
                    {link.indent && links[i - 1] && !links[i - 1].indent && (
                      <p className="eyebrow text-xs mt-4 mb-1.5 pl-4">Par famille</p>
                    )}
                    <button
                      onClick={() => !link.empty && go(link.path)}
                      className={`w-full text-left transition-colors ${
                        link.empty ? "cursor-default" : "hover:text-[#6B1226]"
                      } ${
                        link.indent
                          ? "pl-4 py-3 text-[1.2rem] font-semibold text-[#1C1917] flex items-center gap-3"
                          : "font-display text-[1.45rem] font-semibold text-[#1C1917] py-3"
                      }`}
                      data-testid={link.id}
                      aria-disabled={link.empty || undefined}
                    >
                      {link.indent && <span className={`w-2 h-2 rounded-full shrink-0 ${link.empty ? "bg-[#D6C9B4]" : "bg-[#C5A059]"}`} />}
                      <span className={link.empty ? "text-[#A8A29E]" : ""}>{link.label}</span>
                      {link.empty && (
                        <span className="font-eyebrow text-[0.6rem] tracking-[0.18em] uppercase italic text-[#C5A059] font-normal" data-testid="menu-prochainement">
                          prochainement
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="px-6 py-5 border-t border-[#6B1226]/10" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}>
              <p className="text-xs text-[#78716C] leading-relaxed">
                Paiement au restaurant lors du retrait.
                <br />
                L'abus d'alcool est dangereux pour la santé.
              </p>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
